/**
 * The bounded context: everything a model is ever told about one learner.
 *
 * This is the only shape that crosses from the tab to the Worker's mentor
 * route (docs/mentor-plan.md §5.3), and it lives in the engine rather than in
 * either end for the reason the chat protocol lives in `packages/chat`: two
 * programs agreeing on a shape by eye is two programs that will one day
 * disagree about it silently. The tab builds one, the Worker parses one, and
 * neither declares it.
 *
 * What is *not* here is the point of the file. There is no account, no email,
 * no id. There is no record of typed commands: the page does not have one
 * (`commandDone` in web/src/api.ts records that a command finished, not what
 * it was), and what the learner typed reaches the model only as the screen
 * shows it, in the terminal tail. File contents travel only as the evidence
 * described below, and as the one excerpt on the levels whose work is a
 * document. All of it — names, contents, scrollback — is learner-written and
 * is therefore data in the prompt and never instructions — the prompt says
 * so, and the output filter runs whatever the model was told.
 *
 * Every list of names is capped. A learner who runs `touch {1..9000}.txt` has
 * not found a way to make this product spend money on a prompt the size of
 * their imagination, and the cap is here rather than at the call site because
 * a cap at the call site is a cap somebody forgets on the second call site.
 *
 * Compiles under the engine's bare ES2022 lib, like everything beside it: no
 * DOM, no Node, no crypto.
 */
/** Names per list. Twenty is enough to describe a working tree and not enough to be a payload. */
export const MAX_NAMES = 20;
/**
 * @deprecated There is a transcript now, and it is better than this was.
 *
 * `MAX_REPLIES` bounded `lastReplies`: at most three of the model's own
 * sentences, kept so it would not repeat itself, from before the mentor could
 * hold a conversation. The request carries `history` — both sides, labelled,
 * oldest first, trimmed against a message count and a token ceiling — so what
 * this bounded is now a second, worse memory of the same conversation, holding
 * half of it and unable to say who said what.
 *
 * It is gone from {@link MentorContext} and the Worker no longer reads the
 * field; this constant stays only until the last caller outside this package
 * stops importing it, exactly as `registersSeen`'s alias did.
 */
export const MAX_REPLIES = 3;
/** The document excerpt cap, in characters: the first few KB of the file under review. */
export const MAX_EXCERPT = 6 * 1024;
/*
 * ## The evidence, and why there is any
 *
 * Until 2026-09-23 this shape held file NAMES and the last grade and nothing
 * else, and production showed what that costs. A learner on l09 asked six
 * times in 37 minutes; the model could not see pricing.js, could not see what
 * `node --test` printed and could not see the terminal, so from the third
 * reply on it could only reword the failing check's sentence — "the function
 * does not clamp" — four times, to somebody who was by then editing the tests
 * to make it stop. A mentor that cannot see the work can only restate the
 * requirement, and the learner already has the requirement.
 *
 * So the context now carries what a person looking over the learner's
 * shoulder would look at: the failing check's own output, the files the
 * failing checks and git point at, and the tail of the terminal. All of it is
 * the learner's own sandbox, all of it is DATA in the prompt, none of it is
 * stored, and every piece has a ceiling here, because a cap at the call site
 * is a cap somebody forgets on the second call site.
 */
/** Characters of terminal scrollback, newest kept. */
export const MAX_TERMINAL_CHARS = 4_000;
/** Lines of terminal scrollback, newest kept. */
export const MAX_TERMINAL_LINES = 80;
/** Characters of one failing check's output. */
export const MAX_OUTPUT_CHARS = 1_200;
/** Characters of check output across the whole grade. */
export const MAX_OUTPUTS_TOTAL = 4_000;
/** Files of evidence. */
export const MAX_FILES = 6;
/** Characters of one file's content, and separately of its diff. */
export const MAX_FILE_CHARS = 4_000;
/** Characters of content plus diff across every file. */
export const MAX_FILES_TOTAL = 12_000;
/** At most `MAX_NAMES` entries, copied rather than aliased so the caller's arrays cannot move underneath. */
function names(list) {
    return list.slice(0, MAX_NAMES);
}
/**
 * The newest `max` characters of `text`, with an ellipsis where the front was
 * cut. For output and scrollback, where what happened last is what matters.
 */
export function clipEnd(text, max) {
    if (text.length <= max)
        return text;
    if (max <= 1)
        return text.slice(text.length - Math.max(0, max));
    return `…${text.slice(text.length - (max - 1))}`;
}
/** Control characters other than newline and tab: none of them is text a model should be handed. */
const CONTROL = /[\x00-\x08\x0b-\x1f\x7f]/g;
/**
 * The terminal's newest lines, within both terminal caps.
 *
 * Trailing blank lines go first — the rows under the prompt are empty and
 * would otherwise spend the line budget on nothing. When the character cap
 * bites, the cut lands on a line boundary where there is one, so the model is
 * not handed half a command.
 */
function terminalTail(text) {
    const lines = text.replace(/\r\n?/g, '\n').replace(CONTROL, '').split('\n');
    while (lines.length > 0 && lines[lines.length - 1].trim() === '')
        lines.pop();
    let kept = lines.slice(-MAX_TERMINAL_LINES).join('\n');
    if (kept.length > MAX_TERMINAL_CHARS) {
        kept = kept.slice(kept.length - MAX_TERMINAL_CHARS);
        const nl = kept.indexOf('\n');
        if (nl >= 0 && nl < kept.length - 1)
            kept = kept.slice(nl + 1);
    }
    return kept;
}
/**
 * The files, within every file cap, in the order given — which is the order
 * of priority (`gatherMentorEvidence`). Each file's content is taken before
 * its diff, and the total is spent front to back, so a sixth file never costs
 * the first one its text. A file left with neither is dropped: its name is in
 * `tree` already.
 *
 * Exported because the evidence is capped where it is gathered as well as
 * here (mentor-evidence.ts), and one definition of the caps is the point.
 */
export function capMentorFiles(files) {
    const out = [];
    let room = MAX_FILES_TOTAL;
    for (const f of files) {
        if (out.length >= MAX_FILES || room <= 0)
            break;
        const file = { path: f.path, why: f.why };
        let truncated = f.truncated === true;
        if (typeof f.content === 'string') {
            const content = f.content.slice(0, Math.min(MAX_FILE_CHARS, room));
            if (content.length < f.content.length)
                truncated = true;
            file.content = content;
            room -= content.length;
        }
        if (typeof f.diff === 'string' && f.diff.length > 0) {
            if (room > 0) {
                const diff = f.diff.slice(0, Math.min(MAX_FILE_CHARS, room));
                if (diff.length < f.diff.length)
                    truncated = true;
                file.diff = diff;
                room -= diff.length;
            }
            else {
                truncated = true;
            }
        }
        if (file.content === undefined && file.diff === undefined)
            continue;
        if (truncated)
            file.truncated = true;
        out.push(file);
    }
    return out;
}
/**
 * Build the context from what the session already knows.
 *
 * Objective *titles* are deliberately absent: the bundle the Worker holds has
 * them, along with the failure text and the diagnostics, so sending them from
 * the tab would be paying to transmit something the far end already has and
 * trusts more. What travels is the ids and the pass state — the part that is
 * true of this learner and of nobody else.
 *
 * The evidence is capped here again whatever the caller already did: the
 * outputs per objective and across the grade, the terminal by lines and then
 * by characters, the files by count, by size and in total. A hidden
 * objective's output is dropped even if a caller supplied one — the grader
 * never does, and this is the second lock on the same door.
 */
export function buildMentorContext(input) {
    const { state, grade } = input;
    let outputRoom = MAX_OUTPUTS_TOTAL;
    const context = {
        version: 1,
        grade: grade
            ? {
                passed: grade.passed,
                objectives: grade.objectives.map((o) => {
                    let output;
                    if (!o.hidden && !o.passed && o.output && outputRoom > 0) {
                        output = clipEnd(o.output, Math.min(MAX_OUTPUT_CHARS, outputRoom));
                        outputRoom -= output.length;
                    }
                    return {
                        id: o.id,
                        passed: o.passed,
                        ...(o.skipped ? { skipped: true } : {}),
                        ...(o.message ? { message: o.message } : {}),
                        ...(output ? { output } : {}),
                    };
                }),
                ...(input.gradeSource ? { source: input.gradeSource } : {}),
            }
            : null,
        tree: {
            isRepo: state.isRepo,
            branch: state.head.branch,
            detached: state.head.detached,
            staged: names(state.status.staged),
            unstaged: names(state.status.unstaged),
            untracked: names(state.status.untracked),
            conflicted: names(state.status.conflicted),
        },
        situation: input.situation ?? null,
        checks: input.checks,
        commands: input.commands,
        saves: input.saves,
        hintsUsed: input.hintsUsed,
        minutes: Math.max(0, Math.floor(input.minutes)),
    };
    const terminal = input.terminal ? terminalTail(input.terminal) : '';
    if (terminal)
        context.terminal = terminal;
    const files = input.files ? capMentorFiles(input.files) : [];
    if (files.length > 0)
        context.files = files;
    if (input.document) {
        context.document = { path: input.document.path, excerpt: input.document.excerpt.slice(0, MAX_EXCERPT) };
    }
    return context;
}
//# sourceMappingURL=mentor-context.js.map